import { useMemo, useState } from "react";
import { Button } from '@clearpath/design-system/ui/button';
import {
  OrderType,
  useFederationInboxQuery,
  type FederationInboxOrderFragment,
  type FederationInboxRowFragment,
} from "../__generated__/graphql";
import { FederationInboxRow } from "../components/FederationInboxRow";
import { isActionable } from "../lib/federation-format";

interface InboxRow {
  order: FederationInboxOrderFragment;
  step: FederationInboxRowFragment;
}

/**
 * Default landing page for the vendor app.
 *
 * Shows step_runs of stepType=EXTERNAL_VENDOR that are currently owned by
 * this vendor tenant via a federation_link — i.e. work sent by origin
 * tenants (SKDLA, MyLabCo, etc.) that we need to action.
 *
 * Server-side filter: OrderFilterInput { orderType: FEDERATED_INCOMING }.
 * We flatten each order's EXTERNAL_VENDOR step_runs into the table, then
 * split actionable (non-terminal) vs history (completed/superseded/…) so the
 * "awaiting action" count reflects only genuinely-actionable work.
 */
export function FederationInboxPage() {
  const [showHistory, setShowHistory] = useState(false);
  const { data, loading, error, refetch } = useFederationInboxQuery({
    variables: {
      filter: { orderType: [OrderType.FederatedIncoming] },
      page: { first: 50 },
    },
    // Network-and-cache so the inbox reflects the latest push-updated state
    // while still rendering the cached rows immediately on mount.
    fetchPolicy: "cache-and-network",
  });

  const allRows: InboxRow[] = useMemo(() => {
    const orders = data?.orders.edges ?? [];
    return orders.flatMap(({ node: order }) =>
      (order.stepRuns?.edges ?? []).map(({ node: step }) => ({ order, step })),
    );
  }, [data]);

  const actionable = useMemo(
    () => allRows.filter((r) => isActionable(r.step.status)),
    [allRows],
  );
  const history = useMemo(
    () => allRows.filter((r) => !isActionable(r.step.status)),
    [allRows],
  );

  if (loading && !data) {
    return (
      <section aria-label="Federation inbox" className="inbox-page">
        <p className="loading-row">Loading inbox…</p>
      </section>
    );
  }
  if (error) {
    return (
      <section aria-label="Federation inbox" className="inbox-page">
        <div className="notice notice-danger" role="alert">
          <p>Couldn’t load your inbox.</p>
          <Button type="button" size="sm" variant="outline" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </section>
    );
  }

  const visible = showHistory ? [...actionable, ...history] : actionable;
  const count = actionable.length;

  return (
    <section aria-label="Federation inbox" className="inbox-page">
      <header className="page-head">
        <div>
          <h1>Inbox</h1>
          <p className="page-sub">
            {count === 0
              ? "You’re all caught up — no work awaiting action."
              : `${count} item${count === 1 ? "" : "s"} awaiting your action`}
            {history.length > 0 && (
              <span className="muted"> · {history.length} in history</span>
            )}
          </p>
        </div>
        <div className="page-actions">
          {history.length > 0 && (
            <label className="history-toggle">
              <input
                type="checkbox"
                checked={showHistory}
                onChange={(e) => setShowHistory(e.target.checked)}
              />
              Show completed &amp; superseded
            </label>
          )}
          <Button type="button" size="sm" variant="outline" onClick={() => void refetch()}>
            Refresh
          </Button>
        </div>
      </header>

      {visible.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon" aria-hidden="true">
            ✓
          </div>
          <h2>Nothing in your inbox</h2>
          <p>
            New federated work from your partner labs will appear here the
            moment it’s assigned. Enjoy the quiet.
          </p>
        </div>
      ) : (
        <div className="table-card">
          <table className="inbox">
            <thead>
              <tr>
                <th scope="col">Origin</th>
                <th scope="col">Order &amp; case</th>
                <th scope="col">Work type</th>
                <th scope="col">SLA</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(({ order, step }) => (
                <FederationInboxRow key={step.id} order={order} step={step} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
